import { 
  API_CONFIG, 
  buildUrl, 
  buildHeaders, 
  StorageHelpers
} from '../config/apiConfig.js';

const createPropertyWithImages = async (formData) => {
  try {
    const url = buildUrl(
      API_CONFIG.CONTROLLERS.PROPERTY,
      API_CONFIG.PROPERTY_ACTIONS.CREATE
    );
    const token = StorageHelpers.getToken();

    if (!token) {
      return { success: false, message: 'Authorization required' };
    }

    const response = await fetch(url, {
      method: API_CONFIG.METHODS.POST,
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const text = await response.text();
    let data;

    try {
      data = text ? JSON.parse(text) : { success: false, message: 'Empty response' };
    } catch (e) {
      console.error('❌ Invalid JSON response:', text.substring(0, 200));
      data = { success: false, message: 'Invalid server response' };
    }

    if (!response.ok) {
      console.error('❌ Upload failed:', response.status, data);
      return { success: false, message: data.message || `Upload failed (${response.status})` };
    }

    console.log('✅ Property with images created:', data);
    return data;
  } catch (error) {
    console.error('Error creating property with images:', error);
    return { success: false, message: error.message };
  }
};

export const apiService = {
  // ===== AUTHENTICATION =====
  login: async (adminId, password) => {
    try {
      console.log('📝 Attempting login...');
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.AUTH,
        API_CONFIG.AUTH_ACTIONS.LOGIN
      );
      const response = await fetch(url, {
        method: API_CONFIG.METHODS.POST,
        headers: buildHeaders(null, 'json'),
        body: JSON.stringify({ admin_id: adminId, password }),
      });

      const data = await response.json();
      
      if (data.success && data.token) {
        StorageHelpers.setToken(data.token);
        StorageHelpers.setAdminUser(data.admin);
        console.log('✅ Login successful');
      }
      
      return data;
    } catch (error) {
      console.error('❌ Login error:', error);
      return { success: false, message: error.message };
    }
  },

  register: async (adminId, name, email, password, role = 'admin') => {
    try {
      console.log('📝 Attempting to register admin...');
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.AUTH,
        API_CONFIG.AUTH_ACTIONS.REGISTER
      );
      const token = StorageHelpers.getToken();
      
      if (!token) {
        return { success: false, message: 'Authorization token required' };
      }

      const response = await fetch(url, {
        method: API_CONFIG.METHODS.POST,
        headers: buildHeaders(token, 'json'),
        body: JSON.stringify({ 
          admin_id: adminId, 
          name, 
          email, 
          password, 
          role 
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        console.error('❌ Registration failed:', data);
        return { success: false, message: data.message || 'Registration failed' };
      }

      console.log('✅ Admin registered successfully');
      return data;
    } catch (error) {
      console.error('❌ Registration error:', error);
      return { success: false, message: error.message };
    }
  },

  verifyToken: async () => {
    try {
      console.log('🔍 Verifying token...');
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.AUTH,
        API_CONFIG.AUTH_ACTIONS.VERIFY
      );
      const token = StorageHelpers.getToken();
      
      if (!token) {
        return { success: false, message: 'No token found' };
      }

      const response = await fetch(url, {
        method: API_CONFIG.METHODS.POST,
        headers: buildHeaders(token, 'json'),
      });

      const data = await response.json();
      
      if (data.success) {
        // Update stored admin user info
        StorageHelpers.setAdminUser(data.admin);
        console.log('✅ Token verified');
      } else {
        // Token is invalid, clear storage
        StorageHelpers.clearAll();
        console.warn('⚠️ Token verification failed');
      }

      return data;
    } catch (error) {
      console.error('❌ Token verification error:', error);
      StorageHelpers.clearAll();
      return { success: false, message: error.message };
    }
  },

  logout: async () => {
    try {
      console.log('📤 Logging out...');
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.AUTH,
        API_CONFIG.AUTH_ACTIONS.LOGOUT
      );
      const token = StorageHelpers.getToken();

      const response = await fetch(url, {
        method: API_CONFIG.METHODS.POST,
        headers: buildHeaders(token, 'json'),
      });

      const data = await response.json();
      StorageHelpers.clearAll();
      console.log('✅ Logged out successfully');
      return data;
    } catch (error) {
      console.error('❌ Logout error:', error);
      StorageHelpers.clearAll();
      return { success: true, message: 'Local logout completed' };
    }
  },

  isAuthenticated: () => {
    return !!StorageHelpers.getToken();
  },

  getAuthToken: () => {
    return StorageHelpers.getToken();
  },

  getAdminUser: () => {
    return StorageHelpers.getAdminUser();
  },

  // ===== PROPERTY METHODS =====
  getProperties: async (category = 'all') => {
    try {
      const params = category && category !== 'all' ? { category } : {};
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.PROPERTY,
        API_CONFIG.PROPERTY_ACTIONS.GET_ALL,
        params
      );
      
      const response = await fetch(url);
      const text = await response.text();
      
      if (!text) {
        console.warn('⚠️ Empty response from server');
        return { success: false, data: [] };
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('❌ Invalid JSON response:', text.substring(0, 200));
        return { success: false, data: [] };
      }

      if (!response.ok) {
        console.error(`❌ HTTP ${response.status}:`, data);
        return { success: false, data: [] };
      }

      const properties = data.data || data.records || [];
      return { 
        success: data.success, 
        data: Array.isArray(properties) ? properties : [],
        count: data.count || properties.length
      };
    } catch (error) {
      console.error('❌ Error fetching properties:', error.message);
      return { success: false, data: [] };
    }
  },

  createProperty: async (formData) => {
    try {
      console.log('📝 Creating property...');
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.PROPERTY,
        API_CONFIG.PROPERTY_ACTIONS.CREATE
      );
      const token = StorageHelpers.getToken();

      if (!token) {
        return { success: false, message: 'Authorization required. Please login first.' };
      }

      const response = await fetch(url, {
        method: API_CONFIG.METHODS.POST,
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Creation failed:', data);
        return { success: false, message: data.message || 'Failed to create property' };
      }

      console.log('✅ Property created:', data);
      return data;
    } catch (error) {
      console.error('❌ Error creating property:', error);
      return { success: false, message: error.message };
    }
  },

  createPropertyWithImage: async (formData) => {
    try {
      console.log('📤 Uploading property with image...');
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.PROPERTY,
        API_CONFIG.PROPERTY_ACTIONS.UPLOAD
      );
      const token = StorageHelpers.getToken();

      if (!token) {
        return { success: false, message: 'Authorization required. Please login first.' };
      }

      const response = await fetch(url, {
        method: API_CONFIG.METHODS.POST,
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const text = await response.text();
      let data;

      try {
        data = text ? JSON.parse(text) : { success: false, message: 'Empty response' };
      } catch (e) {
        console.error('❌ Invalid JSON response:', text.substring(0, 200));
        data = { success: false, message: 'Invalid server response' };
      }

      if (!response.ok) {
        console.error('❌ Upload failed:', response.status, data);
        return { success: false, message: data.message || `Upload failed (${response.status})` };
      }

      console.log('✅ Property uploaded:', data);
      return data;
    } catch (error) {
      console.error('❌ Error uploading property:', error);
      return { success: false, message: error.message };
    }
  },

  // ✅ FIX: Delete Property with proper token retrieval and headers
  deleteProperty: async (propertyId) => {
    try {
      console.log('🗑️ Deleting property:', propertyId);
      
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.PROPERTY,
        API_CONFIG.PROPERTY_ACTIONS.DELETE
      );
      
      // Get token with explicit logging
      const token = StorageHelpers.getToken();
      console.log(`🔑 Retrieved token from storage: ${token ? token.substring(0, 20) + '...' : 'NONE'}`);

      if (!token) {
        console.error('❌ No token found in localStorage');
        console.log('📍 localStorage contents:', Object.keys(localStorage));
        return { success: false, message: 'Authorization required. Please login first.' };
      }

      console.log(`📤 URL: ${url}`);
      console.log(`📤 Property ID: ${propertyId}`);
      console.log(`🔑 Full headers being sent:`);
      console.log(`  - Content-Type: application/json`);
      console.log(`  - Authorization: Bearer ${token.substring(0, 20)}...`);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ id: propertyId }),
      });

      console.log(`📊 Response status: ${response.status}`);
      console.log(`📊 Response statusText: ${response.statusText}`);
      
      const data = await response.json();
      console.log(`📊 Response data:`, data);
      
      if (!response.ok) {
        console.error('❌ Delete failed:', response.status, data);
        return { success: false, message: data.message || `Failed to delete property (${response.status})` };
      }
      
      console.log('✅ Property deleted successfully:', data);
      return data;
    } catch (error) {
      console.error('❌ Error deleting property:', error);
      console.error('❌ Error stack:', error.stack);
      return { success: false, message: error.message };
    }
  },

  // ✅ FIX: Delete Banner with proper token and headers
  deleteBanner: async (bannerId) => {
    try {
      console.log('🗑️ Deleting banner:', bannerId);
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.BANNER,
        API_CONFIG.BANNER_ACTIONS.DELETE
      );
      const token = StorageHelpers.getToken();

      if (!token) {
        console.error('❌ No token found');
        return { success: false, message: 'Authorization required. Please login first.' };
      }

      console.log(`🔑 Token: ${token.substring(0, 20)}...`);
      console.log(`📤 URL: ${url}`);
      console.log(`📤 Banner ID: ${bannerId}`);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ id: bannerId }),
      });

      console.log(`📊 Response status: ${response.status}`);
      
      const data = await response.json();
      console.log(`📊 Response data:`, data);
      
      if (!response.ok) {
        console.error('❌ Delete failed:', response.status, data);
        return { success: false, message: data.message || 'Failed to delete banner' };
      }
      
      console.log('✅ Banner deleted:', data);
      return data;
    } catch (error) {
      console.error('❌ Error deleting banner:', error);
      return { success: false, message: error.message };
    }
  },

  // ✅ FIX: Delete Expert with proper token and headers
  deleteExpert: async (expertId) => {
    try {
      console.log('🗑️ Deleting expert:', expertId);
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.EXPERT,
        API_CONFIG.EXPERT_ACTIONS.DELETE
      );
      const token = StorageHelpers.getToken();

      if (!token) {
        console.error('❌ No token found');
        return { success: false, message: 'Authorization required. Please login first.' };
      }

      console.log(`🔑 Token: ${token.substring(0, 20)}...`);
      console.log(`📤 URL: ${url}`);
      console.log(`📤 Expert ID: ${expertId}`);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ id: expertId }),
      });

      console.log(`📊 Response status: ${response.status}`);
      
      const data = await response.json();
      console.log(`📊 Response data:`, data);
      
      if (!response.ok) {
        console.error('❌ Delete failed:', response.status, data);
        return { success: false, message: data.message || `Failed to delete expert (${response.status})` };
      }
      
      console.log('✅ Expert deleted:', data);
      return data;
    } catch (error) {
      console.error('❌ Error deleting expert:', error);
      return { success: false, message: error.message };
    }
  },

  // ===== SERVICES/EXPERTS =====
  getServices: async (category = 'all') => {
    try {
      const params = category && category !== 'all' ? { field: category } : {};
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.EXPERT,
        API_CONFIG.EXPERT_ACTIONS.GET_ALL,
        params
      );
      
      const response = await fetch(url);
      const text = await response.text();
      
      if (!text) {
        console.warn('⚠️ Empty response from server');
        return { success: false, data: [] };
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('❌ Invalid JSON response:', text.substring(0, 200));
        return { success: false, data: [] };
      }

      if (!response.ok) {
        console.error(`❌ HTTP ${response.status}:`, data);
        return { success: false, data: [], message: data.message };
      }

      return data.success ? { ...data, data: data.data || [] } : { success: false, data: [], message: data.message };
    } catch (error) {
      console.error('❌ Error fetching experts:', error.message);
      return { success: false, data: [], error: error.message };
    }
  },

  getServiceCategories: async () => {
    try {
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.EXPERT,
        API_CONFIG.EXPERT_ACTIONS.GET_CATEGORIES
      );
      const response = await fetch(url);
      const text = await response.text();
      
      if (!text) {
        return { success: false, categories: [] };
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('❌ Invalid JSON:', text.substring(0, 100));
        return { success: false, categories: [] };
      }

      return data.success ? data : { success: false, categories: [] };
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
      return { success: false, categories: [] };
    }
  },

  getServiceById: async (id) => {
    try {
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.EXPERT,
        API_CONFIG.EXPERT_ACTIONS.GET_BY_ID,
        { id }
      );
      const response = await fetch(url);
      const text = await response.text();
      
      if (!text) {
        return { success: false, data: null };
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        return { success: false, data: null };
      }

      return data;
    } catch (error) {
      console.error('❌ Error fetching service:', error);
      return { success: false, data: null };
    }
  },

  createService: async (formData) => {
    try {
      console.log('📝 Creating service/expert...');
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.EXPERT,
        API_CONFIG.EXPERT_ACTIONS.CREATE
      );
      const token = StorageHelpers.getToken();

      if (!token) {
        return { success: false, message: 'Authorization required. Please login first.' };
      }

      const response = await fetch(url, {
        method: API_CONFIG.METHODS.POST,
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Creation failed:', data);
        return { success: false, message: data.message || 'Failed to create service' };
      }

      console.log('✅ Service/Expert created:', data);
      return data;
    } catch (error) {
      console.error('❌ Error creating service:', error);
      return { success: false, message: error.message };
    }
  },

  createServiceWithImage: async (formData) => {
    try {
      console.log('📤 Uploading service/expert with image...');
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.EXPERT,
        API_CONFIG.EXPERT_ACTIONS.CREATE
      );
      const token = StorageHelpers.getToken();

      if (!token) {
        return { success: false, message: 'Authorization required. Please login first.' };
      }

      const response = await fetch(url, {
        method: API_CONFIG.METHODS.POST,
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const text = await response.text();
      let data;

      try {
        data = text ? JSON.parse(text) : { success: false, message: 'Empty response' };
      } catch (e) {
        console.error('❌ Invalid JSON response:', text.substring(0, 200));
        data = { success: false, message: 'Invalid server response' };
      }

      if (!response.ok) {
        console.error('❌ Upload failed:', response.status, data);
        return { success: false, message: data.message || `Upload failed (${response.status})` };
      }

      console.log('✅ Service/Expert uploaded:', data);
      return data;
    } catch (error) {
      console.error('❌ Error uploading service:', error);
      return { success: false, message: error.message };
    }
  },

  createPropertyWithImages,

  getPendingProperties: async () => {
    try {
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.PROPERTY,
        API_CONFIG.PROPERTY_ACTIONS.GET_PENDING
      );
      const response = await fetch(url);
      return response.json();
    } catch (error) {
      console.error('Error fetching pending properties:', error);
      return { success: false, data: [] };
    }
  },

  getPendingServices: async () => {
    try {
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.EXPERT,
        API_CONFIG.EXPERT_ACTIONS.GET_PENDING
      );
      const response = await fetch(url);
      return response.json();
    } catch (error) {
      console.error('Error fetching pending experts:', error);
      return { success: false, data: [] };
    }
  },

  approveProperty: async (propertyId, adminId) => {
    try {
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.PROPERTY,
        API_CONFIG.PROPERTY_ACTIONS.APPROVE
      );
      const token = StorageHelpers.getToken();
      const response = await fetch(url, {
        method: API_CONFIG.METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ id: propertyId, adminId }),
      });
      return response.json();
    } catch (error) {
      console.error('Error approving property:', error);
      return { success: false };
    }
  },

  approveService: async (serviceId, adminId) => {
    try {
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.EXPERT,
        API_CONFIG.EXPERT_ACTIONS.APPROVE
      );
      const token = StorageHelpers.getToken();
      const response = await fetch(url, {
        method: API_CONFIG.METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ id: serviceId, adminId }),
      });
      return response.json();
    } catch (error) {
      console.error('Error approving expert:', error);
      return { success: false };
    }
  },

  rejectProperty: async (propertyId, adminId, reason) => {
    try {
      console.log('❌ Rejecting property:', propertyId);
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.PROPERTY,
        API_CONFIG.PROPERTY_ACTIONS.REJECT
      );
      const token = StorageHelpers.getToken();

      if (!token) {
        return { success: false, message: 'Authorization required' };
      }

      const response = await fetch(url, {
        method: API_CONFIG.METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          id: propertyId, 
          adminId, 
          reason 
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Reject failed:', data);
        return { success: false, message: data.message || 'Failed to reject property' };
      }

      console.log('✅ Property rejected:', data);
      return data;
    } catch (error) {
      console.error('❌ Error rejecting property:', error);
      return { success: false, message: error.message };
    }
  },

  rejectService: async (serviceId, adminId, reason) => {
    try {
      console.log('❌ Rejecting service:', serviceId);
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.EXPERT,
        API_CONFIG.EXPERT_ACTIONS.REJECT
      );
      const token = StorageHelpers.getToken();

      if (!token) {
        return { success: false, message: 'Authorization required' };
      }

      const response = await fetch(url, {
        method: API_CONFIG.METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          id: serviceId, 
          adminId, 
          reason 
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Reject failed:', data);
        return { success: false, message: data.message || 'Failed to reject service' };
      }

      console.log('✅ Service rejected:', data);
      return data;
    } catch (error) {
      console.error('❌ Error rejecting service:', error);
      return { success: false, message: error.message };
    }
  },

  // ===== BANNER METHODS =====
  getBanners: async () => {
    try {
      console.log('📡 Fetching all banners...');
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.BANNER,
        API_CONFIG.BANNER_ACTIONS.GET_ALL
      );
      const response = await fetch(url, {
        method: API_CONFIG.METHODS.GET,
        headers: buildHeaders(null, 'json'),
      });

      if (!response.ok) {
        console.error(`❌ HTTP ${response.status}`);
        return { success: false, data: [], count: 0 };
      }

      const data = await response.json();
      console.log('✅ Banners fetched:', data);
      return data;
    } catch (error) {
      console.error('❌ Error fetching banners:', error);
      return { success: false, data: [], count: 0 };
    }
  },

  createBanner: async (bannerData) => {
    try {
      console.log('📝 Creating banner...');
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.BANNER,
        API_CONFIG.BANNER_ACTIONS.CREATE
      );
      const token = StorageHelpers.getToken();

      if (!token) {
        return { success: false, message: 'Authorization required' };
      }

      const response = await fetch(url, {
        method: API_CONFIG.METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(bannerData),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Banner creation failed:', data);
        return { success: false, message: data.message || 'Failed to create banner' };
      }

      console.log('✅ Banner created:', data);
      return data;
    } catch (error) {
      console.error('❌ Error creating banner:', error);
      return { success: false, message: error.message };
    }
  },

  createBannerWithImage: async (formData) => {
    try {
      console.log('📤 Uploading banner with image...');
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.BANNER,
        API_CONFIG.BANNER_ACTIONS.UPLOAD
      );
      const token = StorageHelpers.getToken();

      if (!token) {
        console.error('❌ No token found');
        return { success: false, message: 'Authorization required. Please login first.' };
      }

      console.log('📝 FormData contents:');
      for (const [k, v] of formData.entries()) {
        console.log(`  ${k}: ${v instanceof File ? v.name : v}`);
      }

      // Validate required fields - Banner API expects: title, subtitle, cta, cta_url, image
      const title = formData.get('title');
      const subtitle = formData.get('subtitle') || formData.get('description'); // Accept both
      const image = formData.get('image');

      if (!title || !subtitle) {
        console.error('❌ Missing title or subtitle');
        return { success: false, message: 'Title and subtitle are required' };
      }

      if (!image) {
        console.error('❌ No image provided');
        return { success: false, message: 'Image is required' };
      }

      if (image.size > 10 * 1024 * 1024) {
        console.error('❌ Image too large:', image.size);
        return { success: false, message: 'Image size must be less than 10MB' };
      }

      console.log(`🔑 Using token: ${token.substring(0, 20)}...`);
      console.log(`📤 Sending to: ${url}`);

      const response = await fetch(url, {
        method: API_CONFIG.METHODS.POST,
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      console.log(`📊 Response status: ${response.status}`);
      console.log(`📊 Response headers:`, Object.fromEntries(response.headers.entries()));

      const text = await response.text();
      console.log(`📄 Response text: ${text.substring(0, 500)}`);
      
      let data;

      try {
        data = text ? JSON.parse(text) : { success: false, message: 'Empty response' };
      } catch (e) {
        console.error('❌ Invalid JSON response:', text.substring(0, 200));
        data = { success: false, message: 'Invalid server response', raw: text };
      }

      if (!response.ok) {
        console.error('❌ Upload failed:', response.status, data);
        return { success: false, message: data.message || `Upload failed (${response.status})` };
      }

      console.log('✅ Banner uploaded:', data);
      return data;
    } catch (error) {
      console.error('❌ Error uploading banner:', error);
      return { success: false, message: error.message };
    }
  },

  updateBanner: async (bannerId, updates) => {
    try {
      console.log('✏️ Updating banner:', bannerId);
      const url = buildUrl(
        API_CONFIG.CONTROLLERS.BANNER,
        API_CONFIG.BANNER_ACTIONS.UPDATE
      );
      const token = StorageHelpers.getToken();

      if (!token) {
        return { success: false, message: 'Authorization required' };
      }

      const response = await fetch(url, {
        method: API_CONFIG.METHODS.POST,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ id: bannerId, ...updates }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Update failed:', data);
        return { success: false, message: data.message || 'Failed to update banner' };
      }

      console.log('✅ Banner updated:', data);
      return data;
    } catch (error) {
      console.error('❌ Error updating banner:', error);
      return { success: false, message: error.message };
    }
  },
};

